<template>
    <div data-testid="user-access-report" class="all-height all-width">
        <div class="pa-6 pb-16 d-flex flex-column gap-6">

            <ReportHeader 
            title="User Access Reports" 
            test-id-prefix="user-access-report" 
            @refresh="fetchInitialData" />

            <div class="d-flex flex-column gap-6">
                <ReportFilter 
                apply-button-text="Generate" 
                cycletype="Actual" 
                :showyear="false" 
                :showperiod="false"
                :showrutype="false" 
                :showSync="false" 
                :showFavourite="false" 
                :showReset="true" 
                :showApply="true"
                :isLoading="filterLoading" 
                :showrole="true" 
                :showreportingunit="true" 
                :shownetwork="true"
                :showregion="true" 
                :showcountry="true" 
                :showmultireportingunit="true" 
                :showmultinetwork="true"
                :showmulticountry="true" 
                :showmultiregion="true"
                :showmultirole="true"
                @newdata="handleFilterData" />
            </div>
            
            <ReportTable 
            :loading="getUserAccessReportListLoading" 
            :headers="getUserAccessReportHeader"
            :items="formattedUserAccessData" 
            :total-pages="getUserAccessReportTotalPages"
            :current-page="currentPage" 
            :items-per-page="itemsPerPage" 
            test-id-prefix="user-access-report"
            :shimmer-slots="shimmerSlots"
            :data-slots="dataSlots" 
            @page-change="handlePageChange"
            @download="downloadFile" 
            @view-details="showDetails"
            @filter-change="handleTableFilterChange">
            <template v-slot:indexno="{ item }">
                <div>
                    {{ item.serial_number }}
                </div>
            </template>
            <template v-slot:role_display="{ item }">
                <div>
                    {{ item.role_display ? getRoleNames(item.role_display) : '' }}
                </div>
            </template>
            </ReportTable>
        </div>

        <ReportDialog 
        v-model="detailsDialog" 
        v-if="detailsDialog && detailsItem" 
        title="User Access Reports"
        :details-item="detailsItem" test-id-prefix="user-access-report-dialog">
            <template v-slot:header-fields="{ item }">
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Roles</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{ getRoleNames(item.role_display) }}
                    </v-col>
                </v-row>
            </template>
        </ReportDialog>
        
    </div>
</template>
<script>
import ReportHeader from './components/ReportHeader.vue';
import ReportTable from './components/ReportTable.vue';
import ReportDialog from './components/ReportDialog.vue';
import { STATUS_VARIANTS } from '@/modules/varianceanalysis/utils/statusConstants.js';
import { PAGINATION, REPORTS_API, FILTER_ROLES_ARRAY, MONTHS } from '@/modules/varianceanalysis/utils/constants.js';
import { mapActions, mapGetters } from 'vuex';
import ReportFilter from '../../views/ReportFilter.vue';

export default {
    name: 'UserAccessReport',

    components: {
        ReportFilter,
        ReportHeader,
        ReportTable,
        ReportDialog
    },

    data() {
        return {
            selectedFilter: {},
            cycletype: 'Actual',
            filterLoading: false,
            filterList: {},
            detailsDialog: false,
            detailsItem: null,
            fetchTimeout: null,
            isInitialLoad: true, // Track if this is the first load
            currentPage: 1,
            itemsPerPage: PAGINATION.DEFAULT_LIMIT,
            statusFilter: [],
            dataSlots: ['indexno', 'role_display', 'created_at', 'status', 'action'],
            shimmerSlots: ['indexno', 'role_display', 'created_at', 'status', 'action'],
        };
    },

    mounted() {
        // Initial data fetch is deferred to handleFilterData when ReportFilter
        // signals loading complete (key='loading', value=false).
    },

    computed: {
        ...mapGetters('varianceanalysis/reports', [
            'getUserAccessReportData',
            'getUserAccessReportListLoading',
            'getUserAccessReportFilterLoading',
            'getUserAccessReportHeader',
            'getUserAccessReportTotalPages',
        ]),

        /**
         * Generates shimmer/skeleton placeholder items for loading state
         * @returns {Array} Array of placeholder items for skeleton loader display
         */
        shimmerItems() {
            return Array.from({ length: 5 }, (_, index) => ({
                __key: index,
                __formatedkey: index,
                _id: index,
                indexno: '',
                role: '',
                created_at: '',
                status: '',
                action: '',
            }));
        },

        /**
         * Reactively formats user access report data for display in the data table
         * @returns {Array} Formatted array of user access report items
         */
        formattedUserAccessData() {
            return this.getUserAccessReportData.map((item, index) => ({
                __key: index,
                __formatedkey: index,
                _id: item._id,
                indexno: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                serial_number: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                ru_codes: item.ru_codes,
                role: item.role,
                // period: item.period,
                // type: item.type,
                errormessage: item.errormessage,
                stage: item.stage,
                status: this.getStages(item.stage),
                created_at: item.created_at,
                created_by: item.created_by,
                user_detail: item.user_detail,
                country_detail: item.country_details,
                region_detail: item.region_details,
                period_detail: item.period_detail,
                reportingunit_detail: item.reportingunit_details,
                network_detail: item.network_details,
                role_display: item.role_display,
                // ru_type: item.ru_type,
            }));
        },
    },


    beforeDestroy() {
        if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
        // Reset component data to prevent stale state
        this.selectedFilter = {};
        this.cycletype = 'Actual';
    },

    methods: {
        /**
         * Handles page change from Pagination component - fetches data for new page
         * @param {Number} page - New page number (1-based from Pagination component)
         */
        handlePageChange(page) {
            this.currentPage = page; // Update current page state for UI
            this.fetchPageData(page - 1); // API uses 0-based page index
        },

        /**
         * Handles status filter changes from DataTableNew column filter
         * @param {Object} filters - Filter object e.g. { status: ['ready', 'failed'] }
         */
        handleTableFilterChange(filters) {
            if (this.isInitialLoad) return; // Skip filter events during initial load
            this.statusFilter = filters.status || [];
            this.currentPage = 1;
            this.fetchPageData(0);
        },

        /**
         * Fetches data for a specific page
         * @param {Number} page - Page number to fetch
         */
        async fetchPageData(page) {
            try {
                await this.fetchUserAccessList({
                    page: page,
                    limit: this.itemsPerPage,
                    cycletype: this.cycletype,
                    type: 'allrucycle_' + this.cycletype.toLowerCase(),
                    ...(this.statusFilter.length ? { status: this.statusFilter } : {})
                });
            } catch (error) {
                console.error('[UserAccessReport]: Error fetching page data', error);
            }
        },
        getMonthName(monthNumber) {
            if (!monthNumber) return '';
            return MONTHS[monthNumber - 1] || monthNumber;
        },
        downloadFile(id) {
            this.$nova.downloadFile(undefined, {}, REPORTS_API.DOWNLOAD + id)
        },
        showDetails(item) {
            this.detailsDialog = true;
            this.detailsItem = item;
        },

        /**
         * Maps stage codes to their corresponding status variant constants
         * @param {Number} stage - The stage code (0=Generating, 1=Ready, 4=Generating, others=Failed)
         * @returns {String} The stage status variant constant
         */
        getStages(stage) {
            switch (stage) {
                case 0:
                    return STATUS_VARIANTS.GENERATING;
                case 1:
                    return STATUS_VARIANTS.READY;
                case 4:
                    return STATUS_VARIANTS.GENERATING;
                case 2:
                    return STATUS_VARIANTS.FAILED;
                default:
                    return STATUS_VARIANTS.FAILED;
            }
        },
        ...mapActions('varianceanalysis/reports', [
            'fetchUserAccessList',
            'fetchUserAccess'
        ]),

        /**
         * Handles filter data changes from GlobalFilter component
         * @param {Object} filterData - Filter data with key-value pairs
         */
        handleFilterData(filterData) {
            // Always update selectedFilter first with incoming data
            if (filterData.key && filterData.value !== undefined) {
                this.$set(this.selectedFilter, filterData.key, filterData.value);
            }

            // Handle initial filter load completion - fetch initial data once filters are ready
            if (filterData.key === 'loading' && filterData.value === false && this.isInitialLoad) {
                this.isInitialLoad = false;
                this.fetchInitialData();
                return;
            }

            // Only fetch filtered data when reload is triggered (Apply button clicked)
            if (filterData.reload) {
                if (this.fetchTimeout) clearTimeout(this.fetchTimeout);

                this.fetchTimeout = setTimeout(() => {
                    this.fetchFilteredData();
                }, 100);
            }
        },

        /**
         * Fetches initial paginated data on component mount
         * API: /v3/varianceanalysis/reportingunit/downloadallaccessexcellist
         */
        async fetchInitialData() {
            try {
                this.currentPage = 1; // Reset to first page (1-based for UI)
                this.statusFilter = []; // Reset status filter
                await this.fetchUserAccessList({ page: 0, limit: this.itemsPerPage, cycletype: this.cycletype, type: 'allrucycle_' + this.cycletype.toLowerCase() });
            } catch (error) {
                console.error('[UserAccessReport]: Error fetching initial data', error);
            }
        },

        /**
         * Fetches filtered cycle report data
         * API: /v2/varianceanalysis/reportingunit/downloadallaccessexcel/
         */
        async fetchFilteredData() {
            console.log(this.selectedFilter, "this.selectedFilter");

            try {
                this.filterLoading = true;
                this.currentPage = 1; // Reset to first page on filter apply
                const payload = {
                    role: this.transformFilterValue(this.selectedFilter.selectedrole),
                    network: this.transformToArray(this.selectedFilter.selectednetwork),
                    country: this.transformToArray(this.selectedFilter.selectedcountry),
                    region: this.transformToArray(this.selectedFilter.selectedregion),
                    reportingunit: this.transformReportingUnit(this.selectedFilter.selectedreportingunit),
                    type: 'allrucycle_' + this.cycletype.toLowerCase(),
                    cycletype: this.cycletype,
                };

                await this.fetchUserAccess(payload);
                await this.fetchInitialData();
            } catch (error) {
                console.error('[UserAccessReport]: Error fetching filtered data', error);
            } finally {
                this.filterLoading = false;
            }
        },

        /**
         * Transforms filter value: returns "all" if empty/null, otherwise returns the value
         * @param {String|null} value - Filter value
         * @returns {String} Transformed value
         */
        transformFilterValue(value) {
            return value || 'all';
        },

        /**
         * Transforms filter value to array: returns "all" if empty/null, otherwise returns array
         * Used for multi-select fields like network, region, country
         * @param {String|Array|null} value - Filter value
         * @returns {String|Array} Transformed value as array or 'all'
         */
        transformToArray(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        /**
         * Transforms reporting unit value: returns "all" if empty or 'all' string, otherwise returns array
         * @param {String|Array|null} value - Reporting unit value
         * @returns {String|Array} Transformed value
         */
        transformReportingUnit(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        getRoleNames(roles) {
            if (!roles) return '';
            // If it's a string, wrap in array
            const roleArray = Array.isArray(roles) ? roles : [roles];

            return roleArray.map(role => {
                const roleKey = role.toLowerCase();
                const matchedRole = FILTER_ROLES_ARRAY.find(r => r._id === roleKey);
                return matchedRole ? matchedRole.name : role;
            }).join(', ');
        },
    },
};
</script>

<style scoped>
.failed-reason {
    color: var(--status-red);
}
</style>
